// a reusable service which can be applied to different top level question modules

// todo: extract this service out of members-www and place independantly

// todo: expand answer type to allow for height and weight types
export type Answer = string | string[] | Date | number | boolean
type GenericQuestionId = string
export type Answers = Map<GenericQuestionId, Answer>

export enum QuestionTypeWithoutOptions {
  READONLY = 'READONLY',
  TEXT = 'TEXT',
  CONSENTBOX = 'CONSENTBOX',
  EMAIL = 'EMAIL',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
}

export enum QuestionTypeWithOptions {
  SINGLESELECT = 'SINGLESELECT',
  MULTISELECT = 'MULTISELECT',
}

export abstract class Question {
  id: GenericQuestionId

  constructor(id: GenericQuestionId) {
    this.id = id
    // todo: consider handling validation in here
  }
}

export class SingleSelectQuestion extends Question {
  readonly type = QuestionTypeWithOptions.SINGLESELECT as const
  options: string[]

  constructor(id: GenericQuestionId, options: string[]) {
    super(id)
    this.options = options
  }
}
export class MultiSelectQuestion extends Question {
  readonly type = QuestionTypeWithOptions.MULTISELECT as const
  options: string[]

  constructor(id: GenericQuestionId, options: string[]) {
    super(id)
    this.options = options
  }
}

export class TextQuestion extends Question {
  readonly type = QuestionTypeWithoutOptions.TEXT as const
}

export class ReadOnlyQuestion extends Question {
  readonly type = QuestionTypeWithoutOptions.READONLY as const
}

export class ConsentQuestion extends Question {
  readonly type = QuestionTypeWithoutOptions.CONSENTBOX as const
}

export class NumberQuestion extends Question {
  readonly type = QuestionTypeWithoutOptions.NUMBER as const
}
export class DateQuestion extends Question {
  readonly type = QuestionTypeWithoutOptions.DATE as const
}

export abstract class RawQuestionModule {
  abstract evaluate(answers: Answers): Question[]
}

export function getApplicableQuestions(
  topLevelQuestions: RawQuestionModule[],
  answers: Answers
): Question[] {
  let applicableQuestions: Question[] = []
  for (let questionModule of topLevelQuestions) {
    let applicableQsInModule = questionModule.evaluate(answers)
    applicableQuestions = applicableQuestions.concat(applicableQsInModule)
  }
  return applicableQuestions
}

export function getFirstQuestion(
  topLevelQuestions: RawQuestionModule[]
): Question {
  // first q is constant regardless of the answers
  const noAnswers = new Map<string, Answer>()
  const applicableQuestions = getApplicableQuestions(
    topLevelQuestions,
    noAnswers
  )
  return applicableQuestions[0]
}

export function getNextQuestion(
  topLevelQuestions: RawQuestionModule[],
  answers: Answers
): Question | null {
  // get applicable questions based on answers so far
  const applicableQuestions = getApplicableQuestions(topLevelQuestions, answers)

  // for every question
  for (let question of applicableQuestions) {
    // see if the question is answered
    const answer = answers.get(question.id)
    // if no answer exists for that q, return it
    if (answer === undefined) {
      return question
    }
  }
  // if all applicable questions are already answered, there are none left
  return null
}

export function getLastAnsweredQuestion(
  topLevelQuestions: RawQuestionModule[],
  answers: Answers
): Question | null {
  // get applicable questions based on answers so far
  // this will own the order
  const applicableQuestions = getApplicableQuestions(topLevelQuestions, answers)

  let lastAnsweredQ
  // for every question
  for (let question of applicableQuestions) {
    const answer = answers.get(question.id)
    // if the question is not answered yet, break
    if (answer === undefined) {
      break
      // if the question is answered, continue
    } else {
      lastAnsweredQ = question
    }
  }
  return lastAnsweredQ ?? null
}
